import { api } from '../api';
import type { Recipe, RecipeIngredient, RecipeShelfLife, RecipeStorageContainer, RecipeTool, RecipeType, ShelfLifeUnit, StorageContainer, Tool } from '@/types/recipe';
import type { PaginationParams, PaginatedResponse } from '@/types/common';
import type { Region } from '@/types/region';

export type RecipeIncludeOption =
  | 'ingredients'
  | 'allergens'
  | 'company'
  | 'variants'
  | 'scopes'
  | 'createdBy'
  | 'updatedBy'
  /** Computed per recipe on the list: see `Recipe.cost`. */
  | 'cost'
  /** The menu item that already links a plated recipe: see `Recipe.menuItem`. */
  | 'menuItem';

export type ScopeType = 'GLOBAL' | 'COMPANY' | 'REGION' | 'LOCATION';

export interface UpsertScopeInput {
  scopeType: ScopeType;
  scopeId: number;
  isExcluded: boolean;
}

export interface RecipeScope {
  recipeId: number;
  scopeType: ScopeType;
  scopeId: number;
  isExcluded: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface RecipeSearchParams extends PaginationParams {
  companyId?: number;
  /** Omitted means both kinds: one list holds prep and plated recipes (T-701). */
  recipeType?: RecipeType;
  category?: string;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  isPublished?: boolean;
  isActive?: boolean;
  createdById?: number;
  excludeAllergens?: string[];
  include?: RecipeIncludeOption[];
}

export interface CreateRecipeInput {
  name: string;
  slug: string;
  description?: string;
  companyId?: number;
  /**
   * Required, and deliberately so: it decides where the recipe can be used, and
   * guessing it wrong puts a dressing on a menu.
   */
  recipeType: RecipeType;
  category?: string;
  subcategory?: string;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  servings?: number;
  /** What one batch makes. Sent as a pair; the API drops half of one. */
  yieldQuantity?: number;
  yieldUom?: string;
  instructions?: unknown[];
}

export interface BulkImportError {
  index: number;
  name: string;
  error: string;
}

export interface BulkImportResult {
  created: number;
  failed: BulkImportError[];
}

export interface IngredientInput {
  /** Exactly one of materialId or subRecipeId — an ingredient, or a recipe. */
  materialId?: number;
  subRecipeId?: number;
  quantity: number;
  unit: string;
  isOptional?: boolean;
  preparationNotes?: string;
  sortOrder?: number;
  groupName?: string;
}

export interface UpdateIngredientInput {
  quantity?: number;
  unit?: string;
  isOptional?: boolean;
  preparationNotes?: string;
  groupName?: string;
}

export interface UpdateRecipeInput {
  name?: string;
  slug?: string;
  description?: string;
  recipeType?: RecipeType;
  category?: string;
  subcategory?: string;
  difficulty?: 'EASY' | 'MEDIUM' | 'HARD';
  isPublished?: boolean;
  labelingRequirements?: string;
  targetSellingPrice?: number | null;
  prepTimeMinutes?: number;
  cookTimeMinutes?: number;
  /** Derived from the yield and the portion when both are sent (T-706). */
  servings?: number;
  yieldQuantity?: number | null;
  yieldUom?: string | null;
  portionQuantity?: number | null;
  portionUom?: string | null;
  instructions?: unknown[];
  imageUrl?: string;
  avatarUrl?: string;
  videoUrl?: string;
}

/**
 * Why a costed line has no number. The UI turns these into the dash's
 * explanation rather than leaving the cook to guess (T-604/T-605).
 */
export type CostUnavailableReason =
  | 'NO_UNIT_COST'
  | 'UNCONVERTIBLE_UNIT'
  | 'SUB_RECIPE_NO_YIELD'
  | 'SUB_RECIPE_INCOMPLETE'
  /** The recipe is, through its sub-recipes, an ingredient of itself. */
  | 'RECIPE_CYCLE';

export interface CostedLine {
  ingredientId: number;
  /** Null when the line is a sub-recipe rather than a bought ingredient. */
  materialId: number | null;
  name: string;
  quantity: number;
  unit: string;
  /** Cost of one of this line's own units, not of one purchase unit. */
  unitCost: number | null;
  extendedCost: number | null;
  source: 'MATERIAL' | 'SUB_RECIPE';
  subRecipeId: number | null;
  /**
   * Set whenever something is off. `SUB_RECIPE_INCOMPLETE` is the one reason
   * that arrives *with* a cost — the number is a floor, not the answer.
   */
  unavailableReason: CostUnavailableReason | null;
  unavailableDetail: string | null;
  /** The line by weight, volume and as purchased; null for a unit the API can't place. */
  measures?: LineMeasures | null;
}

/**
 * A line restated through its ingredient's supplier item (the pack-size
 * converter): weight, volume, the density between them, and the amount to take
 * from the case before trim and peel. A figure the item can't support is null.
 */
export interface LineMeasures {
  family: 'weight' | 'volume' | 'count';
  grams: number | null;
  millilitres: number | null;
  each: number | null;
  densityGPerMl: number | null;
  yieldPct: number | null;
  /** The line's quantity before yield, in the line's own unit. */
  asPurchasedQuantity: number | null;
  supplierName: string | null;
}

export interface CostSummary {
  recipeId: number;
  totalRecipeCost: number;
  /** Alias of `costPerPortion`, kept for callers that predate T-604. */
  costPerUnit: number | null;
  costPerPortion: number | null;
  costPerYieldUnit: number | null;
  recipeYield: { quantity: number; uom: string } | null;
  servings: number | null;
  /** What `costPerPortion` divided by: the plates of a plated yield, else servings. */
  portions: number | null;
  onBudget: boolean | null;
  targetSellingPrice: number | null;
  /** Null whenever there is no selling price to measure against (T-606). */
  foodCostPercent: number | null;
  grossMargin: number | null;
  grossMarginPercent: number | null;
  lines: CostedLine[];
  /** False when some line could not be costed, making the total a floor. */
  isComplete: boolean;
  uncostedLineCount: number;
}

/**
 * A stage of a recipe's method ("Sweat", "med heat · chill"), for the recipe
 * card views: the instruction steps it covers and the lines that join at it.
 * A TASK is mise en place with no ingredients ("Preheat oven to 350°F").
 */
export interface RecipeStage {
  id: number;
  recipeId: number;
  kind: 'STAGE' | 'TASK';
  sortOrder: number;
  action: string;
  detail: string | null;
  stepNumbers: number[];
  ingredientIds: number[];
  /**
   * True (the default) when the stage's outcome goes straight into the next
   * stage; false when it is held and flows into `flowsIntoStageId` instead.
   * Absent on a response that predates held stages, which reads as true.
   */
  includeInNext?: boolean;
  /** Id of the later STAGE a held outcome flows into; null when not held. */
  flowsIntoStageId?: number | null;
}

export interface RecipeStageInput {
  kind: 'STAGE' | 'TASK';
  action: string;
  detail?: string | null;
  stepNumbers: number[];
  ingredientIds: number[];
  /** Defaults to true. A TASK is always true. */
  includeInNext?: boolean;
  /**
   * Required when `includeInNext` is false: the 0-based index, within the
   * submitted stages, of the later STAGE the outcome flows into.
   */
  flowsInto?: number | null;
}

/** One recipe line that uses an ingredient or a prep recipe, with what it costs there. */
export interface RecipeUsageLine {
  lineId: number;
  recipeId: number;
  recipeName: string;
  recipeType: 'PREP' | 'PLATED';
  category: string | null;
  quantity: number;
  unit: string;
  extendedCost: number | null;
  unavailableReason: CostUnavailableReason | null;
}

export interface CreateShelfLifeInput {
  stage: string;
  storageMethod?: string;
  coolingInstructions?: string;
  labelingRequired?: boolean;
  usedByDate?: string;
  /** Sent together or not at all — the server rejects a number with no unit. */
  shelfLifeValue?: number | null;
  shelfLifeUnit?: ShelfLifeUnit | null;
  notes?: string;
  sortOrder?: number;
}

export interface UpdateShelfLifeInput {
  stage?: string;
  storageMethod?: string;
  coolingInstructions?: string;
  labelingRequired?: boolean;
  usedByDate?: string;
  shelfLifeValue?: number | null;
  shelfLifeUnit?: ShelfLifeUnit | null;
  notes?: string;
  sortOrder?: number;
}

export interface RecipeAllergen {
  id: number;
  name: string;
  code: string;
  severity: string;
  sources: string[];
}

// ── StorageContainer catalog ─────────────────────────────────────────────────

export interface CreateStorageContainerInput {
  name: string;
  subType?: string;
  sizeOpen?: string;
}

export interface UpdateStorageContainerInput {
  name?: string;
  subType?: string | null;
  sizeOpen?: string | null;
}

// ── RecipeStorageContainer junction ──────────────────────────────────────────

export interface LinkStorageContainerInput {
  storageContainerId: number;
  qty?: number;
  required?: boolean;
  flag?: string;
  notes?: string;
  sortOrder?: number;
}

export interface UpdateRecipeStorageContainerInput {
  qty?: number;
  required?: boolean;
  flag?: string;
  notes?: string;
  sortOrder?: number;
}

// ── Tool catalog ─────────────────────────────────────────────────────────────

export interface CreateToolInput {
  name: string;
  toolType?: string;
  sizeSpec?: string;
}

export interface UpdateToolInput {
  name?: string;
  /** null clears it. */
  toolType?: string | null;
  sizeSpec?: string | null;
}

// ── RecipeTool junction ───────────────────────────────────────────────────────

export interface LinkToolInput {
  toolId: number;
  qty?: number;
  required?: boolean;
  flag?: string;
  notes?: string;
  sortOrder?: number;
}

export interface UpdateRecipeToolInput {
  qty?: number;
  required?: boolean;
  flag?: string;
  notes?: string;
  sortOrder?: number;
}

export interface CheckAvailabilityInput {
  locationId: number;
}

export interface CreateRecipeVersionInput {
  versionNote?: string;
}

export interface CheckAvailabilityResponse {
  recipeId: number;
  locationId: number;
  isAvailable: boolean;
  missingMaterials: {
    materialId: number;
    materialName: string;
    required: number;
    available: number;
    unit: string;
  }[];
  checkedAt: string;
}

function buildSearchParams(params?: RecipeSearchParams): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  if (params.page != null) sp.set('page', params.page.toString());
  if (params.limit != null) sp.set('limit', params.limit.toString());
  if (params.sortBy) sp.set('sortBy', params.sortBy);
  if (params.sortOrder) sp.set('sortOrder', params.sortOrder);
  if (params.search) sp.set('search', params.search);
  if (params.companyId != null) sp.set('companyId', params.companyId.toString());
  if (params.category) sp.set('category', params.category);
  if (params.recipeType) sp.set('recipeType', params.recipeType);
  if (params.difficulty) sp.set('difficulty', params.difficulty);
  if (params.isPublished !== undefined) sp.set('isPublished', String(params.isPublished));
  if (params.isActive !== undefined) sp.set('isActive', String(params.isActive));
  if (params.createdById != null) sp.set('createdById', params.createdById.toString());
  if (params.excludeAllergens?.length) {
    for (const code of params.excludeAllergens) sp.append('excludeAllergens', code);
  }
  if (params.include?.length) {
    for (const rel of params.include) sp.append('include', rel);
  }
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

export const recipeRequests = {
  list: (params?: RecipeSearchParams) =>
    api.get<PaginatedResponse<Recipe>>(`/recipes${buildSearchParams(params)}`),

  getById: (id: string | number, include: RecipeIncludeOption[] = ['createdBy', 'updatedBy']) => {
    const sp = new URLSearchParams();
    for (const rel of include) sp.append('include', rel);
    const qs = sp.toString();
    return api.get<Recipe>(`/recipes/${id}${qs ? `?${qs}` : ''}`);
  },

  getCategories: (companyId?: number) =>
    api.get<string[]>(`/recipes/categories${companyId != null ? `?companyId=${companyId}` : ''}`),

  createSection: (name: string, companyId?: number) =>
    api.post<{ name: string }>('/recipes/sections', { name, companyId }),

  deleteSection: (name: string, companyId?: number) =>
    api.delete(`/recipes/sections/${encodeURIComponent(name)}${companyId != null ? `?companyId=${companyId}` : ''}`),

  getAllergens: (id: string | number) =>
    api.get<RecipeAllergen[]>(`/recipes/${id}/allergens`),

  checkAvailability: (id: string | number, data: CheckAvailabilityInput) =>
    api.post<CheckAvailabilityResponse>(`/recipes/${id}/check-availability`, data),

  create: (data: CreateRecipeInput) => api.post<Recipe>('/recipes', data),

  bulkCreate: (items: CreateRecipeInput[], organizationId?: number) =>
    api.post<BulkImportResult>('/recipes/bulk-create', { items, organizationId }),

  update: (id: string | number, data: UpdateRecipeInput) =>
    api.patch<Recipe>(`/recipes/${id}`, data),

  delete: (id: string | number) => api.delete(`/recipes/${id}`),

  replaceIngredients: (id: string | number, ingredients: IngredientInput[]) =>
    api.put<RecipeIngredient[]>(`/recipes/${id}/ingredients`, { ingredients }),

  addIngredient: (id: string | number, data: IngredientInput) =>
    api.post<RecipeIngredient[]>(`/recipes/${id}/ingredients`, data),

  getIngredients: (id: string | number, params?: { limit?: number; page?: number }) => {
    const sp = new URLSearchParams();
    if (params?.limit) sp.set('limit', String(params.limit));
    if (params?.page) sp.set('page', String(params.page));
    const qs = sp.toString();
    return api.get<PaginatedResponse<RecipeIngredient>>(`/recipes/${id}/ingredients${qs ? `?${qs}` : ''}`);
  },

  /** The method's stages, with the ingredient lines that join at each. */
  getStages: (recipeId: number) => api.get<RecipeStage[]>(`/recipes/${recipeId}/stages`),

  /** Replaces the whole list of stages, in order. */
  replaceStages: (recipeId: number, stages: RecipeStageInput[]) =>
    api.put<RecipeStage[]>(`/recipes/${recipeId}/stages`, { stages }),

  removeIngredient: (id: string | number, ingredientId: string | number) =>
    api.delete<RecipeIngredient[]>(`/recipes/${id}/ingredients/${ingredientId}`),

  updateIngredient: (id: string | number, ingredientId: string | number, data: UpdateIngredientInput) =>
    api.patch<RecipeIngredient[]>(`/recipes/${id}/ingredients/${ingredientId}`, data),

  bulkDelete: (ids: number[]) =>
    api.post<{ count: number }>('/recipes/bulk-delete', { ids }),

  bulkUpdateStatus: (ids: number[], isPublished: boolean) =>
    api.post<{ count: number }>('/recipes/bulk-update-status', { ids, isPublished }),

  duplicate: (id: string | number) =>
    api.post<Recipe>(`/recipes/${id}/duplicate`, {}),

  createVersion: (id: string | number, data: CreateRecipeVersionInput) =>
    api.post<Recipe>(`/recipes/${id}/versions`, data),

  getAvailability: (
    id: string | number,
    params?: PaginationParams & { isActive?: boolean; companyId?: number; include?: string[] },
  ) => {
    const query = new URLSearchParams();
    if (params) {
      const { include, ...rest } = params;
      Object.entries(rest).forEach(([k, v]) => {
        if (v !== undefined && v !== null) query.set(k, String(v));
      });
      include?.forEach((v) => query.append('include', v));
    }
    const qs = query.toString();
    return api.get<PaginatedResponse<Region>>(`/recipes/${id}/availability${qs ? `?${qs}` : ''}`);
  },

  getScopes: (id: string | number) =>
    api.get<RecipeScope[]>(`/recipes/${id}/scopes`),

  upsertScope: (id: string | number, data: UpsertScopeInput) =>
    api.post<RecipeScope>(`/recipes/${id}/scopes`, data),

  deleteScope: (id: string | number, scopeType: ScopeType, scopeId: string | number) =>
    api.delete(`/recipes/${id}/scopes/${scopeType}/${scopeId}`),

  getCostSummary: (recipeId: string | number) =>
    api.get<CostSummary>(`/recipes/${recipeId}/cost-summary`),

  /** Recipes that use an ingredient, with quantity and line cost. */
  getMaterialUsage: (materialId: number) =>
    api.get<RecipeUsageLine[]>(`/recipes/uses-material/${materialId}`),

  /** Recipes a prep recipe goes into, with quantity and line cost. */
  getUsedIn: (recipeId: number) =>
    api.get<RecipeUsageLine[]>(`/recipes/${recipeId}/used-in`),

  getShelfLife: (recipeId: string, params?: PaginationParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', params.page.toString());
    if (params?.limit != null) sp.set('limit', params.limit.toString());
    const qs = sp.toString();
    return api.get<PaginatedResponse<RecipeShelfLife>>(
      `/recipes/${recipeId}/shelf-life${qs ? `?${qs}` : ''}`,
    );
  },

  createShelfLife: (recipeId: string, data: CreateShelfLifeInput) =>
    api.post<RecipeShelfLife>(`/recipes/${recipeId}/shelf-life`, data),

  updateShelfLife: (recipeId: string, id: string, data: UpdateShelfLifeInput) =>
    api.patch<RecipeShelfLife>(`/recipes/${recipeId}/shelf-life/${id}`, data),

  deleteShelfLife: (recipeId: string, id: string) =>
    api.delete<{ deleted: boolean }>(`/recipes/${recipeId}/shelf-life/${id}`),

  // ── StorageContainer catalog ─────────────────────────────────────────────────

  listStorageContainersCatalog: (params?: PaginationParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', params.page.toString());
    if (params?.limit != null) sp.set('limit', params.limit.toString());
    if (params?.search) sp.set('search', params.search);
    const qs = sp.toString();
    return api.get<PaginatedResponse<StorageContainer>>(`/storage-containers${qs ? `?${qs}` : ''}`);
  },

  createStorageContainerCatalog: (data: CreateStorageContainerInput) =>
    api.post<StorageContainer>('/storage-containers', data),

  updateStorageContainerCatalog: (id: number, data: UpdateStorageContainerInput) =>
    api.patch<StorageContainer>(`/storage-containers/${id}`, data),

  deleteStorageContainerCatalog: (id: number) =>
    api.delete(`/storage-containers/${id}`),

  // ── RecipeStorageContainer junction ──────────────────────────────────────────

  getStorageContainers: (recipeId: number, params?: PaginationParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', params.page.toString());
    if (params?.limit != null) sp.set('limit', params.limit.toString());
    const qs = sp.toString();
    return api.get<PaginatedResponse<RecipeStorageContainer>>(
      `/recipes/${recipeId}/storage-containers${qs ? `?${qs}` : ''}`,
    );
  },

  linkStorageContainer: (recipeId: number, data: LinkStorageContainerInput) =>
    api.post<RecipeStorageContainer>(`/recipes/${recipeId}/storage-containers`, data),

  updateRecipeStorageContainer: (recipeId: number, id: number, data: UpdateRecipeStorageContainerInput) =>
    api.patch<RecipeStorageContainer>(`/recipes/${recipeId}/storage-containers/${id}`, data),

  unlinkStorageContainer: (recipeId: number, id: number) =>
    api.delete<{ deleted: boolean }>(`/recipes/${recipeId}/storage-containers/${id}`),

  // ── Tool catalog ─────────────────────────────────────────────────────────────

  listToolsCatalog: (params?: PaginationParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', params.page.toString());
    if (params?.limit != null) sp.set('limit', params.limit.toString());
    if (params?.search) sp.set('search', params.search);
    const qs = sp.toString();
    return api.get<PaginatedResponse<Tool>>(`/tools${qs ? `?${qs}` : ''}`);
  },

  createToolCatalog: (data: CreateToolInput) =>
    api.post<Tool>('/tools', data),

  updateToolCatalog: (id: number, data: UpdateToolInput) =>
    api.patch<Tool>(`/tools/${id}`, data),

  deleteToolCatalog: (id: number) =>
    api.delete(`/tools/${id}`),

  // ── RecipeTool junction ───────────────────────────────────────────────────────

  getTools: (recipeId: number, params?: PaginationParams) => {
    const sp = new URLSearchParams();
    if (params?.page != null) sp.set('page', params.page.toString());
    if (params?.limit != null) sp.set('limit', params.limit.toString());
    const qs = sp.toString();
    return api.get<PaginatedResponse<RecipeTool>>(
      `/recipes/${recipeId}/tools${qs ? `?${qs}` : ''}`,
    );
  },

  linkTool: (recipeId: number, data: LinkToolInput) =>
    api.post<RecipeTool>(`/recipes/${recipeId}/tools`, data),

  updateRecipeTool: (recipeId: number, id: number, data: UpdateRecipeToolInput) =>
    api.patch<RecipeTool>(`/recipes/${recipeId}/tools/${id}`, data),

  unlinkTool: (recipeId: number, id: number) =>
    api.delete<{ deleted: boolean }>(`/recipes/${recipeId}/tools/${id}`),
};
